import Category from '@Client/runner.directives/common/Category/Category';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { RunnerPageDescription } from '@Client/runner.directives/common/PageDescription';
import { getService } from '@Client/runner.hooks/useService';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import React, { useEffect, useState } from 'react';
import IProcedureSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSummary';
import ProcedureSummary from './ProcedureSummary';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';

type Props = {
  backFromState?: string;
};

const Procedures = (props: Props) => {
  const { backFromState } = props;
  const APP_CONFIG = getService('APP_CONFIG');
  const permissionsService = getService('permissionsService');
  const browserUtilsService = getService('browserUtilsService');
  const sessionService = getService('sessionService');
  const $state = getService('$state');
  const proceduresService = getService('proceduresService');
  const appInsightsService = getService('appInsightsService');
  const pubsubService = getService('pubsubService');
  const runnerStartFlowsFormatter = getService('runnerStartFlowsFormatter');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );

  const allCategoriesId = EmptyGuid;
  const isMobile = browserUtilsService.isMobileDevice();

  const [categories, setCategories] = useState<
    ITreeCategory<IProcedureSummary>[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(
    backFromState === 'app.runner.procedure'
      ? proceduresService.selectedCategoryId
      : allCategoriesId
  );
  const [allProcedures, setAllProcedures] = useState<IProcedureSummary[]>([]);
  const [displayedProcedures, setDisplayedProcedures] = useState<
    IProcedureSummary[]
  >([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const [lowerCaseSearchText, setLowerCaseSearchText] = useState('');

  const [emptyPageTitle] = useState<string>(
    'Hi ' + sessionService.getUser().fullName.split(' ')[0]
  );
  const emptyPageText = 'You have no SOPs published to you.';
  const canCreateSop = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.PROCEDURE_CREATE
  );
  const emptyPageButtonText = canCreateSop ? 'Create a new SOP' : '';
  const emptyPageButtonAction = () => {
    if (canCreateSop) {
      $state.go('app.runner.library.procedures');
    }
  };

  useEffect(() => {
    const subscriberId = 'proceduresPage';

    proceduresService.getProcedures().then(setNewProcedures);
    pubsubService.subscribeManyLate(
      ['PROCEDURE_UPDATED', 'PROCEDURE_DELETED', 'PROCEDURE_PUBLISHED'],
      () => proceduresService.getProcedures().then(setNewProceduresRef.current),
      subscriberId
    );

    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const setNewProceduresRef = React.useRef<
    (procedures: IProcedureSummary[]) => void
  >(() => {
    return;
  });
  const setNewProcedures = (procedures: IProcedureSummary[]) => {
    procedures.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
    setAllProcedures(procedures);
    updateDisplayedProcedures(
      procedures,
      lowerCaseSearchText,
      selectedCategoryId
    );

    const newCategories = generateCategories(procedures);
    setCategories(newCategories);
    const findSelectedCategory = (current: ITreeCategory<any>) => {
      if (current.id === selectedCategoryId) {
        return true;
      }
      return current.items.some(findSelectedCategory);
    };
    if (!newCategories.some(findSelectedCategory)) {
      setSelectedCategoryId(allCategoriesId);
      proceduresService.selectedCategoryId = allCategoriesId;
    }

    setIsDataReady(true);
  };
  useEffect(() => {
    setNewProceduresRef.current = setNewProcedures;
  }, [setNewProcedures]);

  const updateDisplayedProcedures = (
    procedures: IProcedureSummary[],
    searchText: string,
    selectedCategoryId: string
  ) => {
    let filteredProcedures = procedures;

    if (searchText) {
      filteredProcedures = procedures.filter(
        (procedure) =>
          procedure.name.toLowerCase().includes(searchText) ||
          procedure.description?.toLowerCase().includes(searchText)
      );
    }

    if (selectedCategoryId && selectedCategoryId !== allCategoriesId) {
      filteredProcedures = filteredProcedures.filter(
        (procedure) => procedure.categoryId === selectedCategoryId
      );
    }

    setDisplayedProcedures(filteredProcedures);
  };

  const updateSearchText = (newText: string) => {
    const lowerCaseNewText = newText.toLowerCase();
    setLowerCaseSearchText(lowerCaseNewText);
    updateDisplayedProcedures(
      allProcedures,
      lowerCaseNewText,
      selectedCategoryId
    );
  };

  const selectCategory = (newCategoryId: Guid) => {
    setSelectedCategoryId(newCategoryId);
    proceduresService.selectedCategoryId = newCategoryId;
    updateDisplayedProcedures(
      allProcedures,
      lowerCaseSearchText,
      newCategoryId
    );
  };

  const generateCategories = (procedures: IProcedureSummary[]) => {
    const proceduresByCategory =
      runnerStartFlowsFormatter.groupObjectsByCategory(procedures);
    const newCategories =
      runnerCategoryNavigationService.convertToTreeViewCompatibleCategories(
        proceduresByCategory
      );
    newCategories.splice(0, 0, {
      id: allCategoriesId,
      text: 'All',
      flowModels: [],
      items: []
    });

    return newCategories.sort((a, b) =>
      a.text.localeCompare(b.text, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  };

  const procedureClicked = (procedureId: string) => {
    $state.go('app.runner.procedure', {
      procedureId: procedureId
    });
  };

  return (
    <div id="procedures">
      <RunnerPageDescription
        showText={!isMobile}
        pageHeaderText="SOPs"
        pageDescription="A list of SOPs published to you."
      />
      {isMobile && categories.length > 0 && (
        <div className="flows-active-content-mobile">
          <Category
            isMobile={isMobile}
            categorisedItems={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={selectCategory}
            defaultItemId={allCategoriesId}
          />
          {displayedProcedures.map((procedure) => (
            <ProcedureSummary
              key={procedure.id}
              procedure={procedure}
              onClick={procedureClicked}
            />
          ))}
        </div>
      )}
      {!isMobile && isDataReady && (
        <div className="procedures-content">
          <Category
            isMobile={isMobile}
            categorisedItems={categories}
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={selectCategory}
            defaultItemId={allCategoriesId}
          />
          <div className="procedures-list">
            <div className="toolbar">
              <Search
                value={lowerCaseSearchText}
                onValueChange={updateSearchText}
                placeholder="Search by SOP name or description"
              />
            </div>
            {displayedProcedures.map((procedure) => (
              <ProcedureSummary
                key={procedure.id}
                procedure={procedure}
                onClick={procedureClicked}
              />
            ))}
            {isDataReady && displayedProcedures.length === 0 && (
              <EmptyState
                summaryText={emptyPageTitle}
                bodyText={emptyPageText}
                buttonText={emptyPageButtonText}
                onButtonClick={emptyPageButtonAction}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Procedures;
